import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import { ThankYouPage } from "@/pages/pay/thank-you-page";
import { getPlanCheckout } from "@/services/planPaymentsService";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 90;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type ReturnPhase = "polling" | "failed";

/**
 * dLocal Go success_url. Two audiences land here:
 *
 *   · The authenticated tenant returning from their own onboarding /
 *     settings checkout — we poll the checkout row until it flips to
 *     paid, then bounce them back to /onboarding or /settings with a
 *     `?plan_payment=done` flag for the celebration UI.
 *
 *   · An UNAUTHED external buyer — the accountant's client following
 *     a shared payment link. dLocal subscription plans ignore the
 *     per-request `success_url` query param and use the dashboard-
 *     configured default, which still points here. For this audience
 *     we render the public thank-you screen directly — no polling, no
 *     API calls. Backend webhook updates state independently; the
 *     accountant's portal tab picks it up via its own poll.
 */
export function DlocalReturnPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();
  const [phase, setPhase] = useState<ReturnPhase>("polling");

  useEffect(() => {
    if (authLoading || !user) return;
    const params = new URLSearchParams(window.location.search);
    // dLocal Go subscription flow: plan-level success_url has no query
    // string (dLocal would otherwise append `?external_id=…` with `?`
    // instead of `&` and corrupt the URL). We recover the checkout id
    // from `external_id` (= checkout_id) and read `returnContext` from
    // the checkout row itself rather than the URL.
    const checkoutId =
      params.get("checkout_id")?.trim() ||
      params.get("external_id")?.trim() ||
      params.get("order_id")?.trim() ||
      null;
    const paymentIdFromRedirect =
      params.get("payment_id")?.trim() ||
      params.get("paymentId")?.trim() ||
      params.get("id")?.trim() ||
      null;

    function destinationFor(
      ctx: string,
      kind: "done" | "failed" | "timeout",
    ): string {
      const settings = ctx === "settings";
      const qs =
        kind === "done" && checkoutId
          ? `&checkout_id=${encodeURIComponent(checkoutId)}`
          : "";
      return settings
        ? `/settings?tab=billing&plan_payment=${kind}${qs}`
        : `/onboarding?plan_payment=${kind}${qs}`;
    }

    if (!checkoutId) {
      setPhase("failed");
      window.location.replace(destinationFor("onboarding", "failed"));
      return;
    }

    let cancelled = false;
    void (async () => {
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (cancelled) return;
        try {
          const row = await getPlanCheckout(checkoutId, paymentIdFromRedirect);
          const ctx = row.returnContext === "settings" ? "settings" : "onboarding";
          if (row.status === "paid") {
            window.location.replace(destinationFor(ctx, "done"));
            return;
          }
          if (row.status === "failed" || row.status === "cancelled") {
            window.location.replace(destinationFor(ctx, "failed"));
            return;
          }
        } catch {
          // keep polling on transient errors
        }
        await sleep(POLL_MS);
      }
      window.location.replace(destinationFor("onboarding", "timeout"));
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  if (authLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (!user) {
    return <ThankYouPage />;
  }

  if (phase === "failed") {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-base font-semibold text-foreground">{t("pay.dlocalReturnFailedTitle")}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t("pay.dlocalReturnFailedMessage")}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <LoadingLogo size="lg" className="mx-auto" />
      <p className="text-base font-semibold text-foreground">{t("pay.dlocalReturnConfirmingTitle")}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{t("pay.dlocalReturnConfirmingMessage")}</p>
    </div>
  );
}
